import { accessToken } from '../modules/auth';

const getUrl = (resource: string, params: Record<string, any> = {}) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL ? process.env.REACT_APP_API_BASE_URL.toString() : '';
    let queryString = new URLSearchParams(params).toString();
    if (queryString) queryString = '?' + queryString;

    return `${baseUrl}${resource}${queryString}`;
};

interface HttpInfo {
    resource: string;
    method: 'GET' | 'POST' | 'DELETE' | 'PATCH' | 'PUT' | 'HEAD' | 'OPTIONS' | 'CONNECT';
    params?: Record<string, any>;
    data?: Record<string, any>;
    safe?: boolean;
    contentType?: string;
}

interface SimpleHttpInfo<TData = Record<string, any>> {
    params?: Record<string, any>;
    data?: TData;
    safe?: boolean;
    contentType?: string;
}

class HttpError extends Error {
    constructor(status: number, message: string) {
        super(message); // (1)
        this.name = 'ValidationError'; // (2)
    }
}

const httpFetch = async <T>(info: HttpInfo): Promise<T | null> => {    
    const { resource, method, params, data, safe } = info;
    const { getAcquireTokenSilent } = accessToken();
    const token = await getAcquireTokenSilent();
    const options = {
        method: method,
        body: typeof data === 'object' ? JSON.stringify(data) : null,
        headers: {
            'Content-type': 'application/json',
            Authorization: safe ? `Bearer ${token?.accessToken}` : '',
        },
    };

    const request = new Request(getUrl(resource, params), options);
    const response = await fetch(request);

    if (!response.ok) {
        throw new HttpError(response.status, response.statusText);
    }

    try {
        const fetched: T = await response.json();
        return fetched;
    } catch (err) {
        // may error if there is no body, return empty array
        return null;
    }
};

const httpGet = async <T>(resource: string, info?: SimpleHttpInfo): Promise<T | null> => {
    return await httpFetch<T>({ method: 'GET', resource, ...info });
};

const httpPost = async <T, U>(resource: string, info: any): Promise<T | null> => {
    return await httpFetch<T>({ method: 'POST', resource, ...info });
};

const httpPut = async <T, U>(resource: string, info: any): Promise<T | null> => {
    return await httpFetch<T>({ method: 'PUT', resource, ...info });
};

const httpPatch = async <T, U>(resource: string, info: any): Promise<T | null> => {
    return await httpFetch<T>({ method: 'PATCH', resource, ...info });
};

const httpDelete = async <T>(resource: string, info: SimpleHttpInfo): Promise<T | null> => {
    return await httpFetch<T>({ method: 'DELETE', resource, ...info });
};

export default {
    fetch: httpFetch,
    get: httpGet,
    post: httpPost,
    put: httpPut,
    patch: httpPatch,
    delete: httpDelete,
};
